import { useEffect, useState, useContext } from "react";
import { useChangeBooleans, useComponentProperties, useFetchedValues, useResetActions, useResetAttributeTable, useResetComponentProperties, useSaveComponent, useUpdateSyncComponentAttributes } from "../../../Hooks/Display/useComponentProperties";
import { useSelectedComponent } from "../../../Hooks/Display/useComponents";
import { usePortalPages, useSelectedPage, useUpdatePageComponent } from "../../../Hooks/Display/usePages";
import { usePropsStoreValue } from "../../../Hooks/PropStore/usePropsStore";
import ComponentToolbar from "./ComponentToolbar/ComponentToolbar";
import Scrollable from "../../Shared/Edit/Scrollable";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { Checkbox } from "@progress/kendo-react-inputs";
import { DropDownList, ComboBox } from "@progress/kendo-react-dropdowns";
import componentPropertiesMap from "./ComponentFormProperties.json";
import AttributeTable from "../AttributeTable/AttributeTable";
import ActionTable from "../ActionTable/ActionTable";
import AttributeFormBuilder from "../FormBuilder/AttributeFormBuilder";
import useInternalUserCheck from "../../../Hooks/Misc/useInternalUserCheck";
import { useTranslation } from "react-i18next";
import { GlobalContext } from "../../../Renderers/GlobalContext";
import { getAttributes } from "../../../API/Attribute/Attribute";
import RemoveDialog from "../../Shared/Remove/RemoveDialog";
import DataFilter from "./Shared/DataFilter";

function Cards() {

    const ALLOWED_TARGET_PAGE_TYPES = ["Table", "Calendar", "Split View"]

    const [oneToThirty, setOneToThirty] = useState([...Array(30).keys()].map(key => ({
        text: String(key + 1),
        value: String(key + 1)
    })));

    const [LabelOrientations, setLabelOrientations] = useState([
        { text: "Left", value: "Left" },
        { text: "Top", value: "Top" }
    ]);

    const [coverOptions, setCoverOptions] = useState([
        { text: "Fit to Card", value: "FitToCard" },
        { text: "Fill Card", value: "FillCard" }
    ]);

    const [CardRows, setCardRows] = useState([...oneToThirty]);
    const [ExtraRows, setExtraRows] = useState([...oneToThirty.slice(0, 5)]);
    const [Records, setRecord] = useState([
        { text: "Show All", value: "Show All" },
        { text: "Show More", value: "Show More" }
    ]);

    // State for inline edit confirmation dialogs
    const [showEnableInlineEditDialog, setShowEnableInlineEditDialog] = useState(false);
    const [showDisableInlineEditDialog, setShowDisableInlineEditDialog] = useState(false);

    const formProperties = componentPropertiesMap.Cards;
    const { user } = useContext(GlobalContext).state;
    // hooks
    const selectedPage = useSelectedPage();
    const selectedComponent = useSelectedComponent();
    const accessedFromDesigner = usePropsStoreValue("accessedFromDesigner");
    const advancedMode = usePropsStoreValue("advancedMode");
    const entityId = usePropsStoreValue("entityId");
    const isInternalUser = useInternalUserCheck(user.email);
    const saveComponent = useSaveComponent();
    const updateSyncedComponentAttributes = useUpdateSyncComponentAttributes();
    const updatePageComponent = useUpdatePageComponent(selectedPage.parentId, selectedPage._id)
    const resetContext = useResetComponentProperties();
    const resetAttributes = useResetAttributeTable();
    const resetActions = useResetActions();

    const portalPages = usePortalPages();
    const targetPages = portalPages.filter(page => page.useAsReference === selectedPage.useAsReference && ALLOWED_TARGET_PAGE_TYPES.includes(page.subPageType));

    const [properties, setProperty] = useComponentProperties();
    const [{
        isGeneralPropertiesChanged,
        isActionTableChanged,
        isAttributeTableChanged
    }, setChangeProperty] = useChangeBooleans();
    const [{ generalProperties: fetchedValues }, { updateGeneralFetchedValue }] = useFetchedValues();

    const { t, i18n, ready: tReady } = useTranslation([
        "display/common",
        "display/component_toolbar",
        "display/components/cards",
        "display/tables/attribute"
    ]);

    useEffect(() => {
        if (selectedPage && selectedComponent) {
            setPropertiesWithAttributes();
        }
    }, [selectedPage, selectedComponent])

    useEffect(() => {
        if (tReady) {
            setLabelOrientations([
                { text: t("label_orientation_options.left", { ns: "display/components/cards" }), value: "Left" },
                { text: t("label_orientation_options.top", { ns: "display/components/cards" }), value: "Top" }
            ])
            setRecord([
                { text: t("record_options.show_all", { ns: "display/components/cards" }), value: "Show All" },
                { text: t("record_options.show_more", { ns: "display/components/cards" }), value: "Show More" }
            ])
            setCoverOptions([
                { text: t("cover_image_options.fit_to_card", { ns: "display/components/cards" }), value: "FitToCard" },
                { text: t("cover_image_options.fill_card", { ns: "display/components/cards" }), value: "FillCard" }
            ])
            const newOneToThirty = [...Array(30).keys()].map(key => ({
                text: t("numbers." + (key + 1), { ns: "display/common" }),
                value: String(key + 1)
            }));
            setOneToThirty(newOneToThirty);
            setCardRows(newOneToThirty);
            setExtraRows(newOneToThirty.slice(0, 5));
        }
    }, [tReady])

    // componentWillUnmount
    useEffect(() => {
        return () => {
            resetContext();
        }
    }, [])

    useEffect(() => {
        setIsChanged();
    }, [properties])
    // state
    const [tabSelected, setSelectedTab] = useState(0);

    async function setPropertiesWithAttributes() {
        const attributes = await getAttributes(entityId);
        const imageAttributes = attributes.filter(attribute => attribute.attributeType === "Image");
        for (const property of formProperties) {
            switch (property) {
                case "targetPage":
                    const page = targetPages.find(page => page._id === selectedComponent[property]);
                    setProperty(property, page);
                    break;
                case "coverImageAttribute":
                    const attribute = imageAttributes.find(attr => attr._id === selectedComponent[property]);
                    setProperty(property, attribute);
                    break;
                default:
                    if (
                        selectedComponent[property] !== undefined
                    ) {
                        setProperty(property, selectedComponent[property])
                    }
                    break;
            }
        }
        setChangeProperty("isGeneralPropertiesChanged", false);
        updateGeneralFetchedValue("attributes", imageAttributes);
    }

    function handleTabSelect(event) {
        setSelectedTab(event.selected)
    }

    function handleInputChange(event) {
        const { name, value } = event.target;
        const newValue = typeof value === "object" ? value.value : value;
        setProperty(event.target.name, newValue);
    }

    function handleInlineEditChange(event) {
        const newValue = event.target.value;
        const currentValue = properties.inlineEdit;

        // If the value is actually changing, show appropriate confirmation dialog
        if (newValue !== currentValue) {
            if (newValue) {
                setShowEnableInlineEditDialog(true);
            } else {
                setShowDisableInlineEditDialog(true);
            }
        }
    }

    function handleEnableInlineEditConfirm() {
        setProperty("inlineEdit", true);
        setShowEnableInlineEditDialog(false);
    }

    function handleEnableInlineEditCancel() {
        setShowEnableInlineEditDialog(false);
    }

    function handleDisableInlineEditConfirm() {
        setProperty("inlineEdit", false);
        setShowDisableInlineEditDialog(false);
    }

    function handleDisableInlineEditCancel() {
        setShowDisableInlineEditDialog(false);
    }



    function handleShowCoverImage(event) {
        const { name, value } = event.target;
        if (value === false) {
            setProperty("coverImageAttribute", "");
            setProperty("coverImageOption", "FitToCard");
        } else {
            if (!properties.coverImageOption) {
                setProperty("coverImageOption", "FitToCard");
            }
        }
        setProperty(name, value);
    }

    function handleAttributeDropdown(event) {
        setProperty(event.target.name, event.target.value && event.target.value._id ? event.target.value : "");
    }

    function handleCoverImageOption(event) {
        setProperty(event.target.name, event.target.value.value);
    }

    function getCoverImageOption() {
        return coverOptions.find(option => option.value === properties.coverImageOption);
    }

    function handlePages(event) {
    }

    function setIsChanged() {
        let isChanged = false;
        for (const property of formProperties) {
            if (property === "inlineEdit" && properties.inlineEdit !== selectedComponent.inlineEdit) {
                isChanged = true;
            }
            switch (property) {
                case "targetPage":
                    if (
                        selectedComponent[property] && !properties[property] ||
                        selectedComponent[property] && properties[property] && selectedComponent[property]._id !== properties[property]._id ||
                        !selectedComponent[property] && properties[property]
                    ) {
                        isChanged = true;
                    }
                    break;
                case "coverImageAttribute":
                    if (
                        selectedComponent[property] && !properties[property] ||
                        selectedComponent[property] && properties[property] && selectedComponent[property] !== properties[property]._id ||
                        !selectedComponent[property] && properties[property]
                    ) {
                        isChanged = true;
                    }
                    break;
                case "showFilter":
                    if (
                        selectedComponent[property] !== properties[property]
                    ) {
                        isChanged = true;
                    }
                    break;
                case "filterLabel":
                    if (
                        selectedComponent[property] !== properties[property]
                    ) {
                        isChanged = true;
                    }
                    break;
                case "filterAttributes":
                    if (
                        selectedComponent[property] !== properties[property]
                    ) {
                        isChanged = true;
                    }
                    break;
                default:
                    if (
                        selectedComponent[property] !== undefined &&
                        selectedComponent[property] !== properties[property]
                    ) {
                        isChanged = true;
                    }
                    break;
            }
        }
        // if (properties.showCoverImage && !properties.coverImageAttribute) {
        //     debugger;
        //     isChanged = false;
        // }
        setChangeProperty("isGeneralPropertiesChanged", isChanged);
    }

    async function handleSave() {
        const updatedComponent = await saveComponent();
        updatePageComponent(selectedComponent.id, updatedComponent);
        if (isAttributeTableChanged && selectedComponent.componentsToSync && selectedComponent.componentsToSync.length > 0) {
            updateSyncedComponentAttributes();
        }
    }

    function resetComponent() {
        if (isGeneralPropertiesChanged) {
            setPropertiesWithAttributes();
        }
        if (isAttributeTableChanged) {
            resetAttributes();
        }
        if (isActionTableChanged) {
            resetActions();
        }
    }

    function getValueFromArray(array, value) {
        return array.find(item => item.value === value);
    }

    return (
        <div className={"h-100 d-flex flex-column line" + (!accessedFromDesigner && " runtimePopup")}>
            <ComponentToolbar onSave={handleSave} onReset={resetComponent} />
            <Scrollable noPadding={true}>
                <TabStrip className="flex-grow-1 h-100 edit1" selected={tabSelected} onSelect={handleTabSelect}>
                    <TabStripTab title={t("tabs.general", { ns: "display/common" })}>
                        <Scrollable>
                            <form className="p-2 edit2">
                                <Checkbox
                                    disabled={selectedComponent.hidden}
                                    checked={properties.showLocalSearch}
                                    name={"showLocalSearch"}
                                    onChange={handleInputChange}
                                    label={t("form_labels.local_search", { ns: "display/components/cards" })}
                                    className="my-1 d-block"
                                />
                                {
                                    accessedFromDesigner &&
                                    <>
                                        <Checkbox
                                            name="showEntityIcon"
                                            disabled={selectedComponent.hidden}
                                            checked={properties.showEntityIcon}
                                            onChange={handleInputChange}
                                            label={t("form_labels.entity_icon", { ns: "display/components/cards" })}
                                            className="my-1 d-block"
                                        />
                                        <div className="form-label">
                                            {t("form_labels.property_label_orientation", { ns: "display/components/cards" })}
                                        </div>
                                        <DropDownList
                                            name="labelOrientation"
                                            disabled={selectedComponent.hidden}
                                            onChange={handleInputChange}
                                            className="form-control-custom w-25"
                                            value={getValueFromArray(LabelOrientations, properties.labelOrientation)}
                                            data={LabelOrientations}
                                            textField="text"
                                            dataItemKey="value"
                                        />

                                    </>
                                }
                                {!properties.inlineEdit && (
                                    <>
                                        <div className="form-label">
                                            {t("form_labels.num_property_columns", { ns: "display/components/cards" })}
                                        </div>
                                        <DropDownList
                                            name="attributeColumn"
                                            disabled={selectedComponent.hidden}
                                            onChange={handleInputChange}
                                            className="form-control-custom w-25"
                                            value={getValueFromArray(oneToThirty, properties.attributeColumn)}
                                            data={oneToThirty.slice(0, 3)}
                                            textField="text"
                                            dataItemKey="value"
                                        />
                                    </>
                                )}
                                <div className="form-label">
                                    {t("form_labels.num_card_columns", { ns: "display/components/cards" })}
                                </div>
                                <DropDownList
                                    name="cardColumn"
                                    disabled={selectedComponent.hidden}
                                    onChange={handleInputChange}
                                    className="form-control-custom w-25"
                                    value={getValueFromArray(oneToThirty, properties.cardColumn)}
                                    data={oneToThirty.slice(0, 4)}
                                    textField="text"
                                    dataItemKey="value"
                                />
                                <div>
                                    <Checkbox
                                        name="showCoverImage"
                                        label={t("form_labels.display_cover_image", { ns: "display/components/cards" })}
                                        disabled={selectedComponent.hidden}
                                        value={properties.showCoverImage}
                                        onChange={handleShowCoverImage}
                                    />
                                    {
                                        properties.showCoverImage &&
                                        <>
                                            <div className="form-label">
                                                {t("form_labels.display_cover_image_attribute", { ns: "display/components/cards" })}
                                            </div>
                                            <div className="clearBtn">
                                                <ComboBox
                                                    name="coverImageAttribute"
                                                    className="w-25 select-overflow"
                                                    disabled={selectedComponent.hidden}
                                                    onChange={handleAttributeDropdown}
                                                    value={properties.coverImageAttribute}
                                                    textField='attributeName'
                                                    data={fetchedValues.attributes}
                                                    dataItemKey='_id'
                                                />
                                            </div>
                                            <div className="form-label"
                                                title="Fit to Card: Scale the image to fit entirely within the card dimensions, which may result in empty spaces (letterboxing) on two sides.
Fill Card: Scale the image to fill the entire card area, cropping parts of the image if necessary."
                                            >
                                                {t("form_labels.display_cover_image_options", { ns: "display/components/cards" })}
                                            </div>
                                            <div className="w-25 mt-3">
                                                <DropDownList
                                                    name="coverImageOption"
                                                    disabled={selectedComponent.hidden}
                                                    value={getCoverImageOption()}
                                                    data={coverOptions}
                                                    textField="text"
                                                    dataItemKey="value"
                                                    onChange={handleCoverImageOption}
                                                />
                                            </div>
                                        </>
                                    }
                                </div>
                                <Checkbox
                                    name="inlineEdit"
                                    disabled={selectedComponent.hidden}
                                    value={properties.inlineEdit}
                                    onChange={handleInlineEditChange}
                                    label={t("form_labels.inline_edit", { ns: "display/components/cards" })}
                                    className="my-1 d-block"
                                />
                                {
                                    accessedFromDesigner &&
                                    <>
                                        <div className="form-label">
                                            {t("form_labels.num_card_rows", { ns: "display/components/cards" })}
                                        </div>
                                        <DropDownList
                                            name="cardRows"
                                            disabled={selectedComponent.hidden}
                                            onChange={handleInputChange}
                                            className="form-control-custom w-25"
                                            value={getValueFromArray(CardRows, properties.cardRows)}
                                            data={CardRows}
                                            textField="text"
                                            dataItemKey="value"
                                        />
                                        <div className="form-label">
                                            {t("form_labels.more_records_option", { ns: "display/components/cards" })}
                                        </div>
                                        <DropDownList
                                            name="records"
                                            disabled={selectedComponent.hidden}
                                            onChange={handleInputChange}
                                            className="form-control-custom w-25"
                                            value={getValueFromArray(Records, properties.records)}
                                            data={Records}
                                            textField="text"
                                            dataItemKey="value"
                                        />
                                        {properties.records === "Show More" ? (
                                            <>
                                                <div className="form-label">
                                                    {t("form_labels.num_extra_rows", { ns: "display/components/cards" })}
                                                </div>
                                                <DropDownList
                                                    name="extraRows"
                                                    disabled={selectedComponent.hidden}
                                                    onChange={handleInputChange}
                                                    className="form-control-custom w-25"
                                                    value={getValueFromArray(ExtraRows, properties.extraRows)}
                                                    data={ExtraRows}
                                                    textField="text"
                                                    dataItemKey="value"
                                                />
                                            </>
                                        ) : null}
                                        {properties.records === "Show All" ? (
                                            <>
                                                <div className="form-label">
                                                    {t("form_labels.target_page", { ns: "display/components/cards" })}
                                                </div>
                                                <DropDownList
                                                    name="page"
                                                    disabled={selectedComponent.hidden}
                                                    onChange={handlePages}
                                                    className="form-control-custom w-25"
                                                    value={properties.page}
                                                    data={targetPages}
                                                    textField={"pageName"}
                                                    dataItemKey={"_id"}
                                                />
                                            </>
                                        ) : (
                                            ""
                                        )}
                                    </>
                                }
                                {!selectedPage.useAsReference && (
                                    <DataFilter />
                                )}
                            </form>
                        </Scrollable>
                    </TabStripTab>
                    <TabStripTab title={properties.inlineEdit ? t("tabs.form_builder", { ns: "display/common" }) : t("tabs.properties", { ns: "display/common" })}>
                        {properties.inlineEdit ? <AttributeFormBuilder /> : <AttributeTable />}
                    </TabStripTab>
                    <TabStripTab title={t("tabs.actions", { ns: "display/common" })}>
                        <ActionTable />
                    </TabStripTab>
                </TabStrip>
            </Scrollable>
            {showEnableInlineEditDialog && (
                <RemoveDialog
                    toggleDeleteDialog={handleEnableInlineEditCancel}
                    onDelete={handleEnableInlineEditConfirm}
                    message={t("inline_edit.enable_dialog.message", { ns: "display/components/cards" })}
                    Title={t("inline_edit.enable_dialog.title", { ns: "display/components/cards" })}
                    deleteButtonText={t("inline_edit.enable_dialog.buttons.enable", { ns: "display/components/cards" })}
                    cancelButtonText={t("inline_edit.enable_dialog.buttons.cancel", { ns: "display/components/cards" })}
                />
            )}
            {showDisableInlineEditDialog && (
                <RemoveDialog
                    toggleDeleteDialog={handleDisableInlineEditCancel}
                    onDelete={handleDisableInlineEditConfirm}
                    message={t("inline_edit.disable_dialog.message", { ns: "display/components/cards" })}
                    Title={t("inline_edit.disable_dialog.title", { ns: "display/components/cards" })}
                    deleteButtonText={t("inline_edit.disable_dialog.buttons.disable", { ns: "display/components/cards" })}
                    cancelButtonText={t("inline_edit.disable_dialog.buttons.cancel", { ns: "display/components/cards" })}
                />
            )}
        </div>
    )
}

export default Cards;